import { useTranslation } from "react-i18next";
import { Button, Spinner, Text } from "@/shared/ui";
import { useUsage } from "../hooks/useUsage";
import {
  findResource,
  isUnlimited,
  percentUsed,
  type ResourceType,
  type ResourceUsage,
} from "../lib/usage";

/** Resources we surface, in display order, with their label keys. */
const METER_RESOURCES: { type: ResourceType; labelKey: string }[] = [
  { type: "credit", labelKey: "settings.billing.usage.premiumCredits" },
  { type: "basic_request", labelKey: "settings.billing.usage.basicRequests" },
];

function MeterRow({ resource, labelKey }: { resource: ResourceUsage; labelKey: string }) {
  const { t } = useTranslation();
  const unlimited = isUnlimited(resource);
  const percent = percentUsed(resource);

  return (
    <div className="flex flex-col gap-2 py-4">
      <div className="flex items-baseline justify-between gap-4">
        <Text variant="ui" as="span">
          {t(labelKey)}
        </Text>
        <Text variant="body" as="span" className="text-[13px] tabular-nums text-ink-muted">
          {unlimited
            ? t("settings.billing.usage.unlimitedUsed", { used: resource.used })
            : t("settings.billing.usage.usedOfTotal", {
                used: resource.used,
                total: resource.total,
              })}
        </Text>
      </div>

      {percent != null ? (
        <div
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t(labelKey)}
          className="h-1.5 w-full overflow-hidden rounded-pill bg-line-soft"
        >
          <span
            className="block h-full rounded-pill bg-ink transition-[width] duration-300 ease-house"
            style={{ width: `${percent}%` }}
          />
        </div>
      ) : null}

      {!unlimited ? (
        <Text variant="body" as="span" className="text-[13px] text-ink-faint">
          {t("settings.billing.usage.left", { left: resource.left })}
        </Text>
      ) : null}

      {resource.soft_cap_hit ? (
        <Text variant="body" as="span" className="text-[13px] text-ink-soft">
          {t("settings.billing.usage.softCapHit")}
        </Text>
      ) : null}
    </div>
  );
}

/**
 * Credit + basic-request meters for the current period. Ships all three states.
 * `premium` gates the premium-credits row: free-plan users can't spend premium
 * credits, so showing them a "0 / 2000" premium meter is just confusing — hide it.
 */
export function UsageMeter({ premium = true }: { premium?: boolean }) {
  const { t } = useTranslation();
  const { data, isPending, isError, error, refetch } = useUsage();

  if (isPending) {
    return (
      <div className="flex min-h-24 items-center justify-center">
        <Spinner size={20} label={t("common.loading")} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Text variant="body" className="text-ink-soft">
          {t("settings.billing.loadError")}
        </Text>
        <Text variant="body" className="text-[13px] text-ink-faint">
          {error.message}
        </Text>
        <Button variant="ghost" size="sm" onClick={() => void refetch()}>
          {t("settings.billing.retry")}
        </Button>
      </div>
    );
  }

  const rows = METER_RESOURCES.filter(({ type }) => premium || type !== "credit")
    .map(({ type, labelKey }) => ({
      labelKey,
      resource: findResource(data, type),
    }))
    .filter((row): row is { labelKey: string; resource: ResourceUsage } => row.resource != null);

  if (rows.length === 0) {
    return (
      <Text variant="body" className="text-ink-faint">
        {t("settings.billing.usage.empty")}
      </Text>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {rows.map(({ resource, labelKey }) => (
        <MeterRow key={labelKey} resource={resource} labelKey={labelKey} />
      ))}
    </div>
  );
}
